import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  PAPEIS,
  ROTULO_PAPEL,
  type Papel,
  type UsuarioDoEscritorio,
} from '@social/shared';
import { Modal } from '../componentes/modal';
import { Botao, Campo, Entrada, Selecao } from '../componentes/ui';
import { api } from '../lib/sessao';

/** Mesmo mínimo do autocadastro e da conta: é uma senha NASCENDO. */
export const SENHA_MINIMA = 12;

/*
  Os três diálogos da gestão de equipe.

  ## Por que DIÁLOGO, e não formulário empilhado na página
  Criar usuário, trocar papel e definir senha são ESCOLHAS — pedem a atenção
  inteira, e o que está atrás não ajuda a decidir. É a distinção que o próprio
  `Modal` documenta contra o `Painel`: painel para editar ao lado da lista,
  modal para escolher. Como formulários na página, cada um empurrava a tabela
  para baixo e a pessoa perdia de vista o quadro que estava administrando.

  Arquivo próprio pelo motivo de sempre aqui: a página monta e consulta, estas
  peças desenham. Juntas, `usuarios.tsx` passaria das 600 linhas do lint.
*/

export function DialogoNovoUsuario({
  aberto,
  aoFechar,
  aoCriar,
  aoFalhar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoCriar: () => Promise<void>;
  aoFalhar: (e: unknown) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [papel, setPapel] = useState<Papel>('contador');

  // Reabrir com o que ficou da vez anterior devolveria a senha de outra pessoa
  // digitada na tela.
  useEffect(() => {
    if (!aberto) return;
    setNome('');
    setEmail('');
    setSenha('');
    setPapel('contador');
  }, [aberto]);

  const criar = useMutation({
    mutationFn: (dados: { nome: string; email: string; senha: string; papel: Papel }) =>
      api.post<UsuarioDoEscritorio>('/usuarios', dados),
    onSuccess: aoCriar,
    onError: aoFalhar,
  });

  const senhaCurta = senha.length > 0 && senha.length < SENHA_MINIMA;
  const pronto =
    nome.trim().length >= 3 && email.length > 0 && senha.length >= SENHA_MINIMA;

  function enviar(e: FormEvent): void {
    e.preventDefault();
    criar.mutate({ nome: nome.trim(), email: email.trim(), senha, papel });
  }

  return (
    <Modal
      aberto={aberto}
      titulo="Novo usuário"
      descricao="Ele entra com a senha inicial e a troca depois em Minha conta."
      tamanho="medio"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundaria" onClick={aoFechar}>
            Cancelar
          </Botao>
          {/*
            `form` aponta para o formulário do corpo: o botão vive no rodapé do
            modal, fora da árvore do `<form>`, e sem isto o submit não sairia.
          */}
          <Botao
            type="submit"
            form="form-novo-usuario"
            disabled={!pronto || criar.isPending}
          >
            {criar.isPending ? 'Criando…' : 'Criar usuário'}
          </Botao>
        </>
      }
    >
      <form id="form-novo-usuario" onSubmit={enviar} className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Campo rotulo="Nome" id="novo-nome" obrigatorio>
            <Entrada
              id="novo-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </Campo>
          <Campo rotulo="E-mail" id="novo-email" obrigatorio>
            <Entrada
              id="novo-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Campo rotulo="Papel" id="novo-papel" obrigatorio>
            <Selecao
              id="novo-papel"
              value={papel}
              onChange={(e) => setPapel(e.target.value as Papel)}
            >
              {PAPEIS.map((p) => (
                <option key={p} value={p}>
                  {ROTULO_PAPEL[p]}
                </option>
              ))}
            </Selecao>
          </Campo>
          <Campo
            rotulo="Senha inicial"
            id="novo-senha"
            obrigatorio
            dica={`Mínimo de ${String(SENHA_MINIMA)}. Entregue à pessoa.`}
          >
            <Entrada
              id="novo-senha"
              type="password"
              autoComplete="new-password"
              minLength={SENHA_MINIMA}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
          </Campo>
        </div>

        {senhaCurta && (
          <p className="text-erro-600 text-sm" aria-live="polite">
            Faltam {SENHA_MINIMA - senha.length} caracteres.
          </p>
        )}
      </form>
    </Modal>
  );
}

/**
 * Trocar o papel de alguém.
 *
 * Era um `<select>` solto na linha da lista, que aplicava a mudança no
 * `onChange`. Virou diálogo por dois motivos: mudar o papel de alguém não é
 * ajuste de aparência que se faz de passagem, e o `<select>` inline não tinha
 * onde dizer o que a mudança significa — agora o próprio diálogo explica.
 */
export function DialogoPapel({
  usuario,
  aoFechar,
  aoAlterar,
  aoFalhar,
}: {
  usuario: UsuarioDoEscritorio | null;
  aoFechar: () => void;
  aoAlterar: (u: UsuarioDoEscritorio, novo: Papel) => Promise<void>;
  aoFalhar: (e: unknown) => void;
}): JSX.Element {
  const [papel, setPapel] = useState<Papel>('contador');

  useEffect(() => {
    if (usuario) setPapel(usuario.papel);
  }, [usuario]);

  const alterar = useMutation({
    mutationFn: (v: { id: string; papel: Papel }) =>
      api.patch<UsuarioDoEscritorio>(`/usuarios/${v.id}`, { papel: v.papel }),
    onSuccess: async (_r, v) => {
      if (usuario) await aoAlterar(usuario, v.papel);
    },
    onError: aoFalhar,
  });

  return (
    <Modal
      aberto={usuario !== null}
      titulo={usuario ? `Papel de ${usuario.nome}` : 'Papel'}
      descricao="Administrador gerencia a equipe e o plano. Contador gera atos. Assistente só consulta."
      tamanho="pequeno"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundaria" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={!usuario || papel === usuario.papel || alterar.isPending}
            onClick={() => {
              if (usuario) alterar.mutate({ id: usuario.id, papel });
            }}
          >
            {alterar.isPending ? 'Salvando…' : 'Salvar papel'}
          </Botao>
        </>
      }
    >
      <Campo rotulo="Papel" id="papel-novo" obrigatorio>
        <Selecao
          id="papel-novo"
          value={papel}
          onChange={(e) => setPapel(e.target.value as Papel)}
        >
          {PAPEIS.map((p) => (
            <option key={p} value={p}>
              {ROTULO_PAPEL[p]}
            </option>
          ))}
        </Selecao>
      </Campo>
    </Modal>
  );
}

/**
 * O administrador DEFINE a senha de outra pessoa.
 *
 * NÃO pede a senha atual — ele não a sabe, e é isso que torna a função
 * possível. A proteção equivalente está no servidor: só administrador chega
 * na rota, o alvo é do mesmo escritório, e a troca fica no log.
 */
export function DialogoSenha({
  usuario,
  aoFechar,
  aoDefinir,
  aoFalhar,
}: {
  usuario: UsuarioDoEscritorio | null;
  aoFechar: () => void;
  aoDefinir: (u: UsuarioDoEscritorio) => void;
  aoFalhar: (e: unknown) => void;
}): JSX.Element {
  const [senha, setSenha] = useState('');

  // Fechar e reabrir noutra pessoa NÃO pode carregar a senha digitada antes.
  useEffect(() => {
    setSenha('');
  }, [usuario]);

  const definir = useMutation({
    mutationFn: (v: { id: string; senhaNova: string }) =>
      api.post<{ ok: true }>(`/usuarios/${v.id}/senha`, { senhaNova: v.senhaNova }),
    onSuccess: () => {
      setSenha('');
      if (usuario) aoDefinir(usuario);
    },
    onError: aoFalhar,
  });

  const curta = senha.length > 0 && senha.length < SENHA_MINIMA;

  return (
    <Modal
      aberto={usuario !== null}
      titulo={usuario ? `Definir senha de ${usuario.nome}` : 'Definir senha'}
      descricao="A senha atual não é pedida: você não a sabe. As sessões abertas dessa pessoa serão encerradas."
      tamanho="pequeno"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundaria" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={senha.length < SENHA_MINIMA || definir.isPending}
            onClick={() => {
              if (usuario) definir.mutate({ id: usuario.id, senhaNova: senha });
            }}
          >
            {definir.isPending ? 'Definindo…' : 'Definir senha'}
          </Botao>
        </>
      }
    >
      <Campo
        rotulo="Senha nova"
        id="definir-senha"
        obrigatorio
        dica="A pessoa entra com ela e troca depois em Minha conta."
      >
        <Entrada
          id="definir-senha"
          type="password"
          autoComplete="new-password"
          minLength={SENHA_MINIMA}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
      </Campo>

      {curta && (
        <p className="text-erro-600 pt-2 text-sm" aria-live="polite">
          Faltam {SENHA_MINIMA - senha.length} caracteres.
        </p>
      )}
    </Modal>
  );
}
